import {
  ArchiveCampaignButton,
  ArchiveCampaignDialog,
  ArchivedBanner,
  useOpenTaskNames,
} from "@/components/projects/campaign-archive";
import { ProjectHeader } from "@/components/projects/project-header";
import { useCampaignArchiveState } from "@/hooks/use-campaign-archive-state";
import {
  useArchiveCampaignMutation,
  useReturnCampaignMutation,
  useUnarchiveCampaignMutation,
} from "@/lib/queries/campaign-archive";
import { projectsListQueryOptions, type Project } from "@/lib/queries/projects";
import type {
  CampaignRouteContext,
  ProfileRouteContext,
} from "@/lib/types/route-context";
import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
} from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute(
  "/(org)/o/$orgSlug/p/$profileSlug/c/$campaignId",
)({
  beforeLoad: async ({ context, params }) => {
    const parentContext = context as ProfileRouteContext;
    const { profile, organization } = parentContext;
    const campaignId = parseInt(params.campaignId, 10);

    if (isNaN(campaignId)) {
      throw redirect({
        to: "/o/$orgSlug/projects",
        params: { orgSlug: params.orgSlug },
      });
    }

    const projectsData = await context.queryClient.fetchQuery(
      projectsListQueryOptions(organization.id),
    );

    const campaign = projectsData?.projects.find(
      (p: Project) => p.id === campaignId && p.profileId === profile.id,
    );

    if (!campaign) {
      throw redirect({
        to: "/o/$orgSlug/projects",
        params: { orgSlug: params.orgSlug },
      });
    }

    return {
      campaign,
      programId: campaignId,
    } as Omit<CampaignRouteContext, keyof ProfileRouteContext>;
  },
  component: CampaignLayout,
});

function CampaignLayout() {
  // campaign is guaranteed by beforeLoad — it redirects if not found
  const { campaign: routeCampaign, organization } =
    Route.useRouteContext() as CampaignRouteContext;
  const { campaignId } = Route.useParams();
  const {
    campaign,
    listUpdatedAt,
    interactions,
    isArchived,
    isReturned,
    canArchive,
  } = useCampaignArchiveState(organization.id, routeCampaign);
  const ref = { profileId: campaign.profileId, programId: campaign.id };

  // The campaign page is /c/<campaignId>; anything deeper is a task page.
  const pathname = useLocation({ select: (location) => location.pathname });
  const segments = pathname.split("/").filter(Boolean);
  const onCampaignPage =
    segments.at(-2) === "c" && segments.at(-1) === campaignId;

  const [confirmOpen, setConfirmOpen] = useState(false);
  // Only the campaign page's modal shows these.
  const openTaskNames = useOpenTaskNames(
    organization.id,
    onCampaignPage ? interactions : undefined,
  );
  const archive = useArchiveCampaignMutation(organization.id);
  const unarchive = useUnarchiveCampaignMutation(organization.id);

  const confirmArchive = () =>
    archive.mutate(ref, {
      onSuccess: () => setConfirmOpen(false),
      onError: (error) => toast.error(error.message),
    });
  const doUnarchive = () =>
    unarchive.mutate(ref, { onError: (error) => toast.error(error.message) });

  // EPD-12321: opening a campaign that a new task brought back clears its
  // "New task" badge, but only once the server has confirmed the return: a
  // return seen only in cached tasks is sent for the server to check first.
  // One request at a time, keyed to the campaign and the list update, so
  // another campaign is handled too and a failed request is retried on the
  // next list update, never on a plain re-render.
  const { mutateAsync: clearReturn } = unarchive;
  const { mutateAsync: confirmReturn } = useReturnCampaignMutation(
    organization.id,
  );
  const returnConfirmed = Boolean(campaign.creatorArchive?.returnedAt);
  const sentRef = useRef<{
    campaignKey: string;
    key: string;
    inFlight: boolean;
  } | null>(null);
  useEffect(() => {
    if (!isReturned) return;
    const campaignKey = `${campaign.profileId}-${campaign.id}`;
    const key = `${campaignKey}@${listUpdatedAt}:${returnConfirmed}`;
    const sent = sentRef.current;
    if (sent?.key === key) return;
    if (sent?.inFlight && sent.campaignKey === campaignKey) return;
    sentRef.current = { campaignKey, key, inFlight: true };
    const campaignRef = {
      profileId: campaign.profileId,
      programId: campaign.id,
    };
    // A return the server confirms is cleared straight away.
    const request = returnConfirmed
      ? clearReturn(campaignRef)
      : confirmReturn(campaignRef).then(({ returnedAt }) =>
          returnedAt ? clearReturn(campaignRef) : undefined,
        );
    request
      .catch(() => undefined)
      .finally(() => {
        if (sentRef.current?.key === key) {
          sentRef.current = { campaignKey, key, inFlight: false };
        }
      });
  }, [
    isReturned,
    returnConfirmed,
    clearReturn,
    confirmReturn,
    campaign.profileId,
    campaign.id,
    listUpdatedAt,
  ]);

  return (
    <div className="flex flex-col min-h-screen">
      <div className="px-6 pt-6 mb-4">
        <ProjectHeader
          title={campaign.title}
          brand={{
            name: campaign.client.name,
            logoUrl: campaign.onboardingTemplateConfig?.settings?.logo,
            isTest: campaign.client.isTestClient ?? false,
          }}
          profileName={campaign.profileName}
          splashImageUrl={campaign.splashImageUrl}
          actions={
            // Not while a clear is in flight: its delete could land after
            // the new archive and remove it.
            onCampaignPage && canArchive && !unarchive.isPending ? (
              <ArchiveCampaignButton onClick={() => setConfirmOpen(true)} />
            ) : undefined
          }
        />
        {isArchived && (
          <div className="mt-6">
            <ArchivedBanner
              onUnarchive={doUnarchive}
              isPending={unarchive.isPending}
              actionLabel={
                onCampaignPage ? "Unarchive" : "Unarchive to continue"
              }
            />
          </div>
        )}
      </div>
      {/* EPD-12321: a task link (e.g. from an email) waits for the creator to unarchive. */}
      {(onCampaignPage || !isArchived) && <Outlet />}
      <ArchiveCampaignDialog
        open={confirmOpen}
        onOpenChange={(open) => !archive.isPending && setConfirmOpen(open)}
        campaignTitle={campaign.title}
        openTaskNames={openTaskNames}
        onConfirm={confirmArchive}
        isPending={archive.isPending}
      />
    </div>
  );
}
